
/* Wisent Parts Center — Why Buy section and shipping cutoff countdown.
   Uses the shared cutoff from wisent-shipping.js when available.
   Otherwise uses the original WP Why Buy section settings.
*/
(() => {
  'use strict';

  const mobileMedia = window.matchMedia('(max-width: 989px)');
  const originalPositions = new WeakMap();

  const easternFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  });

  function easternParts(date) {
    const parts = {};

    easternFormatter.formatToParts(date).forEach(({ type, value }) => {
      if (type !== 'literal') parts[type] = Number(value);
    });

    return parts;
  }

  function easternCutoffEpoch(year, month, day, hour, minute) {
    const desiredWallTime = Date.UTC(year, month - 1, day, hour, minute);
    let epoch = desiredWallTime;

    // Account for Eastern Standard / Daylight Saving Time.
    for (let i = 0; i < 4; i += 1) {
      const parts = easternParts(new Date(epoch));

      const actualWallTime = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second
      );

      const difference = actualWallTime - desiredWallTime;

      if (Math.abs(difference) < 1000) break;

      epoch -= difference;
    }

    return epoch;
  }

  // Fallback to original section settings if shared script
  // is not yet available.
  function originalSectionCutoff(now = Date.now()) {
    const config = document.querySelector('[data-wp-why-buy]');

    if (!config) return null;

    const hour = Number(config.dataset.cutoffHour ?? 12);
    const minute = Number(config.dataset.cutoffMinute ?? 0);

    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
      return null;
    }

    const closures = new Set(
      (config.dataset.blackoutDates || '')
        .split(/[\s,;]+/)
        .filter(date => /^\d{4}-\d{2}-\d{2}$/.test(date))
    );

    const eastern = easternParts(new Date(now));

    for (let offset = 0; offset < 370; offset += 1) {
      const date = new Date(Date.UTC(
        eastern.year,
        eastern.month - 1,
        eastern.day + offset
      ));

      const isoDate = date.toISOString().slice(0, 10);
      const weekday = date.getUTCDay();

      // Skip Saturday, Sunday, and configured holidays.
      if (
        weekday === 0 ||
        weekday === 6 ||
        closures.has(isoDate)
      ) {
        continue;
      }

      const epoch = easternCutoffEpoch(
        date.getUTCFullYear(),
        date.getUTCMonth() + 1,
        date.getUTCDate(),
        hour,
        minute
      );

      if (epoch > now) {
        return {
          epoch,
          isToday: offset === 0,
          date: isoDate
        };
      }
    }

    return null;
  }

  // Use the same cutoff calculation as the cart drawer.
  function nextShippingCutoff() {
    if (
      typeof window.WisentShipping?.getNextCutoff === 'function'
    ) {
      return window.WisentShipping.getNextCutoff();
    }

    return originalSectionCutoff();
  }

  // Preserve the original responsive Why Buy section positioning.
  function reposition(section) {
    if (!section.parentNode) return;

    if (!originalPositions.has(section)) {
      const marker = document.createComment(
        'WP Why Buy original position'
      );

      section.parentNode.insertBefore(marker, section);
      originalPositions.set(section, marker);
    }

    const marker = originalPositions.get(section);
    const product = document.querySelector(
      '[data-wp-amazon-product]'
    );

    const target = product && (
      product.querySelector('.wp-amz-crossrefs') ||
      product.querySelector('.wp-amz-buy')
    );

    if (mobileMedia.matches && target) {
      if (
        section.parentElement !== target.parentElement ||
        section.previousElementSibling !== target
      ) {
        target.insertAdjacentElement('afterend', section);
      }
    } else if (
      marker.isConnected &&
      section.previousSibling !== marker
    ) {
      marker.parentNode.insertBefore(
        section,
        marker.nextSibling
      );
    }
  }

  // Update the existing product-page countdown.
  function updateCountdown() {
    const product = document.querySelector(
      '[data-wp-amazon-product]'
    );

    const timer = product?.querySelector(
      '[data-wp-shipping-cutoff]'
    );

    if (!product || !timer) return;

    const variantSelect = product.querySelector(
      '[data-wp-variant-select]'
    );

    const selectedOption = variantSelect?.selectedOptions[0];

    const submitButton = product.querySelector(
      '[data-wp-submit]'
    );

    const available = selectedOption
      ? selectedOption.dataset.available === 'true'
      : submitButton?.disabled === false;

    const specialOrder =
      product.querySelector('[data-wp-stock]')
        ?.classList.contains('is-to-order') ||
      product.dataset.toOrderProduct === 'true';

    // Never show the countdown for unavailable or
    // available-to-order products.
    if (!available || specialOrder) {
      timer.hidden = true;
      return;
    }

    const next = nextShippingCutoff();

    if (!next || !Number.isFinite(next.epoch)) {
      timer.hidden = true;
      return;
    }

    const label = timer.querySelector('[data-wp-countdown-label]');
    const value = timer.querySelector('[data-wp-countdown-value]');

    if (!label || !value) return;

    timer.classList.toggle('is-next-day', !next.isToday);

    if (next.isToday) {
      const totalMinutes = Math.max(
        1,
        Math.ceil((next.epoch - Date.now()) / 60000)
      );

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      label.textContent = 'Processing cutoff in';
      value.textContent = hours
        ? `${hours}h ${minutes}m`
        : `${minutes}m`;
    } else {
      const date = new Date(`${next.date}T12:00:00Z`);

      label.textContent = 'Next processing day:';
      value.textContent = date.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    }

    timer.hidden = false;
  }

  function boot() {
    document
      .querySelectorAll('.shopify-section.wp-why-buy-wrapper')
      .forEach(reposition);

    updateCountdown();
  }

  // Initial page load.
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      { once: true }
    );
  } else {
    boot();
  }

  // Responsive positioning.
  if (mobileMedia.addEventListener) {
    mobileMedia.addEventListener('change', boot);
  } else {
    mobileMedia.addListener(boot);
  }

  // Shopify theme editor support.
  document.addEventListener('shopify:section:load', boot);

  // Refresh countdown when returning to the page.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) updateCountdown();
  });

  // Refresh when a customer changes the product variant.
  document.addEventListener('change', event => {
    if (event.target?.matches('[data-wp-variant-select]')) {
      updateCountdown();
    }
  });

  // Automatically refresh every second, including at cutoff.
  window.setInterval(() => {
    if (!document.hidden) updateCountdown();
  }, 1000);
})();
